import { CaretDownIcon, PlusIcon } from '@/common/components/icons'
import {
  Popover,
  PopoverPanel,
  Transition,
  PopoverButton as PopoverButtonHeadless
} from '@headlessui/react'
import { useAppState } from '@/app/hooks/useAppState'
import { useNavigate } from 'react-router-dom'
import cx from 'classnames'
import { usePermissions } from '@/shared/hooks/usePermissions'
import useI18n from '@/shared/hooks/useI18n'
import { Button, PopoverToggleButton } from '@/common'

export default function InstallationsMenu({
  installationName,
  installations,
  clickNewInstallation
}: {
  installationName: string
  installations: {
    id: string
    name: string
    isActive: boolean
  }[]
  clickNewInstallation: () => void
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { setSelectedInstallationId } = useAppState()
  const { checkPermission } = usePermissions()
  const canCreateInstallation = checkPermission('installation', 'create')

  const onInstallationClick = (installationId: string) => {
    setSelectedInstallationId(installationId)
    navigate(`/dashboard/${installationId}`)
  }

  return (
    <Popover>
      <PopoverToggleButton>
        <div className="space-y-1 text-left">
          <div className="font-semibold text-sm break-words">{installationName}</div>
        </div>
        <CaretDownIcon className="flex-shrink-0 size-6" />
      </PopoverToggleButton>
      <Transition
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <PopoverPanel
          transition
          anchor="top end"
          className="z-40 transition shadow-dropdown border translate-x-8 rounded-xs bg-white duration-200 ease-in-out [--anchor-gap:8px] data-[closed]:-translate-y-1 data-[closed]:opacity-0"
        >
          <div className="space-y-1 min-w-56 max-w-72">
            <section className="space-y-1 px-2 py-2">
              {installations.map(installation => (
                <PopoverButtonHeadless
                  as="button"
                  key={installation.id}
                  className={cx(
                    'border w-full text-sm text-left p-2 rounded-xs font-medium',
                    installation.isActive && 'bg-green-500 border-green-600'
                  )}
                  onClick={() => onInstallationClick(installation.id)}
                >
                  {installation.name}
                </PopoverButtonHeadless>
              ))}
            </section>
            <section className="px-2 py-2 border-t">
              <PopoverButtonHeadless
                as={Button}
                disabled={!canCreateInstallation}
                onClick={() => clickNewInstallation()}
                className="w-full"
                variant="subtle"
                icon={<PlusIcon />}
              >
                {t('common:nav.newInstallation')}
              </PopoverButtonHeadless>
            </section>
          </div>
        </PopoverPanel>
      </Transition>
    </Popover>
  )
}
